import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarCheck, Link2, ShieldX, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { lmsStore } from "../lib/lms";
import { useSeo } from "../lib/seo";
import { LMS_HERO, LMS_SHARE } from "../data/plTeams";
import { PopButton } from "../components/PopButton";
import { OwnerCompetitions, PinInput } from "../components/lms/AdminAccess";

const STEPS = [
  { icon: Link2, title: "Invite your mates", text: "Create a competition and share the join link." },
  { icon: CalendarCheck, title: "Pick a winner", text: "Each gameweek, choose one Premier League team to win." },
  { icon: ShieldX, title: "Draw or lose? You're out", text: "No pick counts as out too. Each team can be used only once." },
  { icon: Trophy, title: "Last one standing wins", text: "Outlast everyone and take the pot." },
];

function CreateForm() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [players, setPlayers] = useState("10");
  const [pot, setPot] = useState("50");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/lms", { name, max_players: Number(players), prize_pot: Number(pot || 0), pin });
      lmsStore.save({ id: data.competition.id, name: data.competition.name, admin: data.admin_token });
      toast.success("Competition created — now invite your players!");
      navigate(`/lms/${data.competition.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <form className="rounded-[22px] border-[3px] border-[#0B1F33] bg-white p-4 shadow-[0_4px_0_#0B1F33]" onSubmit={(e) => { e.preventDefault(); create(); }} data-testid="lms-create-form">
      <h2 className="ua-title text-2xl text-[#0E7A3E] sm:text-3xl">Start a competition</h2>
      <label className="mt-2 block text-xs font-extrabold uppercase tracking-wide">Competition name
        <input className="field mt-1" required maxLength={60} placeholder="e.g. Office LMS 26/27" value={name} onChange={(e) => setName(e.target.value)} data-testid="lms-create-name-input" />
      </label>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="block text-xs font-extrabold uppercase tracking-wide">Number of players
          <input className="field mt-1" type="number" required min={2} max={100} value={players} onChange={(e) => setPlayers(e.target.value)} data-testid="lms-create-players-input" />
        </label>
        <label className="block text-xs font-extrabold uppercase tracking-wide">Prize pot (£)
          <input className="field mt-1" type="number" min={0} step="0.01" value={pot} onChange={(e) => setPot(e.target.value)} data-testid="lms-create-pot-input" />
        </label>
      </div>
      <label className="mt-2 flex flex-wrap items-center gap-2 text-xs font-extrabold uppercase tracking-wide">Admin PIN
        <PinInput value={pin} onChange={setPin} testId="lms-create-pin-input" />
        <span className="basis-full text-[11px] font-bold normal-case tracking-normal text-[#0B1F33]/60 sm:basis-auto">Use it with “I'm the admin” to get admin on any device.</span>
      </label>
      <p className="mt-1 text-[11px] font-bold text-[#0B1F33]/60">The pot is for display only — Dither doesn't handle any money.</p>
      <PopButton type="submit" tone="green" size="lg" className="mt-3 w-full" disabled={busy || !name.trim() || pin.length < 4} data-testid="lms-create-button">Create & get invite link</PopButton>
    </form>
  );
}

export default function LmsHome() {
  useSeo({ title: "Last Man Standing — Free Premier League Prediction Game", description: "Run your own free Last Man Standing football game with friends. No app download needed. Just pick, survive, and win.", keywords: "last man standing football, Premier League prediction game, free football game, survivor football pool, LMS game", image: LMS_SHARE, path: "/lms" });
  const mine = lmsStore.all();
  return (
    <div className="mx-auto max-w-5xl space-y-2 px-2 py-3 sm:space-y-3 sm:px-3" data-testid="lms-home-page">
      <section className="lms-pitch relative grid items-center gap-3 overflow-hidden rounded-[22px] border-[3px] border-[#0B1F33] p-4 text-white shadow-[0_5px_0_#0B1F33] sm:grid-cols-[1fr_auto] sm:p-6">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#FFE14D]">Premier League · 2026/27</p>
          <h1 className="ua-logo mt-1 text-5xl leading-none sm:text-7xl" data-testid="lms-home-title">Last Man Standing</h1>
          <p className="mt-2 max-w-md font-comic text-lg sm:text-xl">One pick a week. One wrong result and you're gone.</p>
        </div>
        <img src={LMS_HERO} alt="Cartoon football standing victorious on a trophy" className="h-40 w-40 justify-self-center rounded-[20px] border-[3px] border-[#0B1F33] object-cover shadow-[0_4px_0_#0B1F33] sm:h-56 sm:w-56 sm:rotate-2" />
      </section>
      <div className="grid gap-2 sm:gap-3 md:grid-cols-[1fr_1.1fr]">
        <section className="grid grid-cols-2 gap-2" data-testid="lms-how-it-works">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-[18px] border-[3px] border-[#0B1F33] bg-[#E3F7EA] p-3 shadow-[0_3px_0_#0B1F33]">
              <span className="flex items-center gap-1.5 font-comic text-sm text-[#0E7A3E]"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0B1F33] text-xs text-white">{i + 1}</span><s.icon className="h-4 w-4" strokeWidth={3} /></span>
              <h3 className="mt-1 font-comic text-base leading-tight">{s.title}</h3>
              <p className="mt-0.5 text-xs font-semibold text-[#0B1F33]/75">{s.text}</p>
            </div>
          ))}
        </section>
        <CreateForm />
      </div>
      <OwnerCompetitions />
      {!!mine.length && (
        <section className="rounded-[22px] border-[3px] border-[#0B1F33] bg-white p-3 shadow-[0_4px_0_#0B1F33]" data-testid="lms-my-competitions">
          <h2 className="ua-title text-2xl text-[#0B1F33]">Your competitions</h2>
          <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {mine.map((c) => (
              <Link key={c.id} to={`/lms/${c.id}`} className="flex items-center justify-between gap-2 rounded-xl border-2 border-[#0B1F33] bg-[#F4FBF6] px-3 py-2 font-comic text-sm transition-transform hover:-translate-y-0.5" data-testid={`lms-my-comp-${c.id}`}>
                <span className="truncate">{c.name || "Competition"}</span>
                <span className="flex items-center gap-1 text-[10px] uppercase">{c.admin && <span className="rounded bg-[#0B1F33] px-1.5 py-0.5 text-white">Admin</span>}<ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
